import { StationApi } from 'api/StationApi'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { ReactComponent as WashingSvg } from 'images/icons/washing.svg'
import { useEffect, useMemo, useState } from 'react'
import { humanizeSnakeCaseLabel } from 'utils/lotInterfaces'
import {
  isRefillLotModel,
  supportsDirtyWaterDrainSteps,
  supportsExtendedDockSteps,
  supportsLimitedWashingV2Steps,
  DIRTY_WATER_DRAIN_STEP_COMMAND,
  EXTENDED_DOCK_STEP_COMMANDS,
  LIMITED_WASHING_V2_STEP_COMMANDS,
} from 'utils/stationLotModel'

import type { useStationControlBoard } from '../../StationControl/useStationControlBoard'
import detailsStyle from '../StationDetails.module.css'
import style from './StationStepsSection.module.css'

const STEPS_REASON_LABELS: Record<string, string> = {
  steps_disabled_by_flag: 'Steps not enabled on server',
  offline: 'Station offline',
  no_telemetry_snapshot: 'No telemetry snapshot',
}

type BoardState = ReturnType<typeof useStationControlBoard>

type StationStepsSectionProps = {
  boardState: BoardState
  stationId: number
}

const StationStepsSection = ({ boardState, stationId }: StationStepsSectionProps) => {
  const {
    board,
    lotGate,
    uiLocked,
    opsBusy,
    awaitingInProgress,
    softwareUpdateInProgress,
    lastOpError,
    runOp,
  } = boardState
  const steps = board?.steps ?? null
  const [selectedCommand, setSelectedCommand] = useState<number | ''>('')
  const [confirmDangerousOpen, setConfirmDangerousOpen] = useState(false)

  const disabledReasons = useMemo(
    () =>
      (lotGate?.stepsDisabledReasons || [])
        .map(reason => STEPS_REASON_LABELS[reason] || reason)
        .filter(Boolean)
        .join('; '),
    [lotGate?.stepsDisabledReasons]
  )

  const commandsForLot = useMemo(() => {
    if (!steps) return []
    const lotModel = lotGate?.lotModel ?? steps.session?.lot_model
    if (supportsDirtyWaterDrainSteps(lotModel)) {
      return steps.commands.filter(
        cmd => cmd.command === 1 || cmd.command === 5 || cmd.command === DIRTY_WATER_DRAIN_STEP_COMMAND
      )
    }
    if (isRefillLotModel(lotModel)) {
      return steps.commands.filter(cmd => cmd.command === 1 || cmd.command === 5)
    }
    if (supportsLimitedWashingV2Steps(lotModel)) {
      return steps.commands.filter(cmd => LIMITED_WASHING_V2_STEP_COMMANDS.has(cmd.command))
    }
    if (!supportsExtendedDockSteps(lotModel)) {
      return steps.commands.filter(
        cmd => cmd.command !== DIRTY_WATER_DRAIN_STEP_COMMAND && !EXTENDED_DOCK_STEP_COMMANDS.has(cmd.command)
      )
    }
    return steps.commands.filter(cmd => cmd.command !== DIRTY_WATER_DRAIN_STEP_COMMAND)
  }, [steps, lotGate?.lotModel])

  const stageOptions: IOption[] = useMemo(
    () =>
      commandsForLot.map(cmd => ({
        value: cmd.command,
        text: `${cmd.command}. ${humanizeSnakeCaseLabel(cmd.name)}`,
      })),
    [commandsForLot]
  )

  const stageCurrentOption = useMemo(
    () => stageOptions.find(opt => opt.value === selectedCommand) ?? stageOptions[0],
    [stageOptions, selectedCommand]
  )

  useEffect(() => {
    if (!commandsForLot.length) {
      setSelectedCommand('')
      return
    }
    if (selectedCommand === '' || !commandsForLot.some(cmd => cmd.command === selectedCommand)) {
      setSelectedCommand(commandsForLot[0].command)
    }
  }, [commandsForLot, selectedCommand])

  const stageInProgress = Boolean(lotGate?.inProgress)
  const liveOccupied = Boolean(lotGate?.occupied)
  const livePending = Boolean(lotGate?.pending)
  const serviceOccupied = Boolean(lotGate?.serviceOccupied)
  const fleetOccupied = Boolean(lotGate?.fleetOccupied)

  const stepsBlocked = !lotGate?.stepsEnabled
  // lot_id 0 is valid — do not use truthiness checks on lotId
  const occupyBlocked =
    stepsBlocked || uiLocked || lotGate?.lotId == null || liveOccupied || livePending || fleetOccupied
  const releaseBlocked =
    stepsBlocked ||
    uiLocked ||
    livePending ||
    !liveOccupied ||
    !serviceOccupied ||
    fleetOccupied ||
    !lotGate?.releaseAllowed
  const stagesBlocked =
    stepsBlocked || uiLocked || livePending || !serviceOccupied || fleetOccupied
  const stageActionBlocked = stagesBlocked || (!stageInProgress && selectedCommand === '')
  const showRelease = serviceOccupied && !fleetOccupied && !livePending

  const statusText = livePending
    ? 'Pending'
    : liveOccupied && lotGate?.robotSn != null
      ? String(lotGate.robotSn)
      : 'Released'
  const statusTitle = disabledReasons || lotGate?.fleetOccupiedReason || undefined

  const occupyButtonTitle = softwareUpdateInProgress
    ? 'Software update in progress'
    : showRelease
      ? lotGate?.releaseBlockedReason ||
        (awaitingInProgress ? 'Stage starting — wait for IN_PROGRESS' : undefined) ||
        undefined
        : liveOccupied
        ? 'Lot already occupied'
        : livePending
          ? 'Occupy pending'
          : fleetOccupied
          ? lotGate?.fleetOccupiedReason || 'Fleet occupied'
          : stepsBlocked
            ? disabledReasons || 'Unavailable'
            : undefined

  const selectedStageMeta = commandsForLot.find(cmd => cmd.command === selectedCommand)
  const selectedStageLabel = selectedStageMeta
    ? humanizeSnakeCaseLabel(selectedStageMeta.name)
    : selectedCommand !== ''
      ? String(selectedCommand)
      : 'stage'

  const sendStageCommand = (command: number, confirmDangerous: boolean) => {
    void runOp(
      () =>
        StationApi.stepsCommand(stationId, command, {
          confirm_dangerous: confirmDangerous,
        }),
      { awaitInProgress: command !== 1 }
    )
  }

  return (
    <div className={style.root}>
      <div className={detailsStyle.tableHeaderRow}>
        <div className={detailsStyle.centered}>Maintenance Tasks</div>
      </div>
      <div
        className={detailsStyle.tableRow}
        style={{ gridTemplateColumns: '1fr 2fr 2fr 3fr' }}
      >
        <div className={detailsStyle.centered} aria-hidden />
        <div className={detailsStyle.textAlignLeft}>Occupation</div>
        <div className={detailsStyle.centered} title={occupyButtonTitle}>
          {showRelease ? (
            <Button.Standart
              className={detailsStyle.controlButton}
              disabled={releaseBlocked}
              loading={uiLocked}
              onClick={() => {
                void runOp(() => StationApi.stepsRelease(stationId))
              }}
              title={occupyButtonTitle}
            >
              Release
            </Button.Standart>
          ) : (
            <Button.Standart
              className={detailsStyle.controlButton}
              disabled={occupyBlocked}
              loading={opsBusy && !liveOccupied && !livePending}
              onClick={() => {
                if (lotGate?.lotId == null) return
                void runOp(() => StationApi.stepsOccupy(stationId, lotGate.lotId!), {
                  errorPrefix: 'Occupy failed',
                })
              }}
              title={occupyButtonTitle}
            >
              Occupy
            </Button.Standart>
          )}
        </div>
        <div className={detailsStyle.percents} title={statusTitle}>
          {statusText || '\u00a0'}
        </div>
      </div>

      {lastOpError ? (
        <div className={style.opError} title={lastOpError}>
          {lastOpError}
        </div>
      ) : null}

      <div
        className={detailsStyle.tableRow}
        style={{ gridTemplateColumns: '1fr 2fr 2fr 3fr' }}
      >
        <div className={detailsStyle.centered}>
          <WashingSvg className={style.rowTaskIcon} />
        </div>
        <div className={detailsStyle.textAlignLeft}>Stage</div>
        <div className={detailsStyle.centered}>
          <Button.Standart
            className={detailsStyle.controlButton}
            disabled={stageActionBlocked}
            loading={uiLocked && serviceOccupied}
            onClick={() => {
              if (stageInProgress) {
                sendStageCommand(1, false)
                return
              }
              if (selectedCommand === '') return
              const meta = commandsForLot.find(cmd => cmd.command === selectedCommand)
              if (meta?.dangerous) {
                setConfirmDangerousOpen(true)
                return
              }
              sendStageCommand(Number(selectedCommand), false)
            }}
            title={stageInProgress ? 'Cancel the running maintenance stage' : undefined}
          >
            {stageInProgress ? 'Cancel' : 'Send'}
          </Button.Standart>
        </div>
        <div className={style.stageCell}>
          <Dropdown
            className={style.stageDropdown}
            disabled={stagesBlocked || stageInProgress}
            options={stageOptions}
            currentOption={stageCurrentOption}
            placeholder="Stage"
            menuFitContent
            noShadow
            onClickOption={option => {
              const value = option.value
              setSelectedCommand(
                typeof value === 'number' ? value : value != null ? Number(value) : ''
              )
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        title={`Run ${selectedStageLabel}?`}
        isOpen={confirmDangerousOpen}
        onClose={() => setConfirmDangerousOpen(false)}
        onConfirmClick={() => {
          if (selectedCommand === '') return
          setConfirmDangerousOpen(false)
          sendStageCommand(Number(selectedCommand), true)
        }}
        confirmText="Confirm"
        danger
      >
        <div className={style.confirmBody}>
          You are about to run a potentially dangerous command. Make sure no robot or people are in the
          station lot.
        </div>
      </ConfirmDialog>
    </div>
  )
}

export default StationStepsSection
